import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { TextButton as Button } from '@oneaudi/unified-web-components';
import { useTranslation } from 'react-i18next';
import { selectDealersList, toggleSelectedDealerId } from '../../reducers/dealersListReducer';
import {
  DealersListWrapper,
  DealersListContainer,
  DealersListPadding,
  BackButtonWrapper,
  ButtonWrapper,
} from './DealersSelector.styles';
import CTA from '../Button';
import { CTA_LIST } from '../../constants/CtaList';
import DealersListInfo from './DealersListInfo';
import useInnerHeight from '../../hooks/useInnerHeight';
import DealersListItem from './DealersListItem';
import { calculateDistance } from '../../utils/calculateDistance';
import { MARKET } from '../../constants/market';

interface DealersListProps {
  onClose: () => void;
  handleViewInventory: () => void;
  isListOpened: boolean;
  disabled: boolean;
}

const DealersList: React.FC<DealersListProps> = ({
  onClose,
  isListOpened,
  disabled,
  handleViewInventory,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const innerHeight = useInnerHeight();
  const { dealersWithinRadius, selectedDealersIds, centerLocation } =
    useSelector(selectDealersList);

  const handleCloseList = () => {
    onClose();
  };

  const handleSelectDealerId = (dealerId: string) => {
    dispatch(toggleSelectedDealerId(dealerId));
  };

  const getDistance = useCallback(
    (dealerPosition: GeoJSON.Point) => {
      const centerLat = centerLocation.geometry.location?.lat;
      const centerLng = centerLocation.geometry.location?.lng;

      if (centerLat === undefined || centerLng === undefined) {
        console.error('Center location coordinates are undefined');
        return 'NaN';
      }

      const dealerLatLng = {
        lat: dealerPosition.coordinates[1],
        lng: dealerPosition.coordinates[0],
      };

      const distance = calculateDistance(centerLat, centerLng, dealerLatLng.lat, dealerLatLng.lng);

      return MARKET === 'CAN' ? distance.toFixed(2) : (distance / 1.609).toFixed(2);
    },
    [centerLocation.geometry.location?.lat, centerLocation.geometry.location?.lng],
  );

  return (
    <DealersListContainer>
      <DealersListPadding>
        <BackButtonWrapper>
          <CTA buttonType={CTA_LIST.BACK_TO_RADIUS} arrowAfter noGap onChange={handleCloseList} />
        </BackButtonWrapper>
        <DealersListInfo
          within={dealersWithinRadius?.length}
          selectedNumber={selectedDealersIds.length}
        />
        <DealersListWrapper>
          {dealersWithinRadius.map((dealer) => {
            return (
              <DealersListItem
                name={dealer.properties.name}
                city={dealer.properties.address.city}
                region={dealer.properties.address.region}
                selected={selectedDealersIds.includes(dealer.properties.id)}
                key={dealer.properties.id}
                dealerId={dealer.properties.id}
                distance={getDistance(dealer.geometry)}
                onChange={handleSelectDealerId}
                dealerProperties={dealer.properties}
              />
            );
          })}
        </DealersListWrapper>
        <ButtonWrapper isListOpened={isListOpened}>
          <Button disabled={disabled} variant="primary" isFullWidth onClick={handleViewInventory}>
            {t('dealerLocationSearch.dealersList.save')}
          </Button>
        </ButtonWrapper>
      </DealersListPadding>
    </DealersListContainer>
  );
};

export default DealersList;
